import moment from 'moment';
import { stepsStatuses } from 'constants/constants';
import { STATUSES } from 'constants/user';
import { STATUS_TYPES, type StatusType } from '_atoms/Status';
import type { CandidateListItem } from 'store/reducers/candidateReducer';

export interface CandidateJobAssignmentRow {
  job_name?: string;
  step_type?: string;
  assignment_status?: string;
}

export interface CandidateListApiItem extends CandidateListItem {
  speciality_name?: string;
  job_assignments?: CandidateJobAssignmentRow[];
}

export interface MappedCandidateRow {
  candidate: {
    uuid: string;
    fullNameEn?: string;
    fullNameRu?: string;
  };
  speciality_name?: string;
  job_assignments: CandidateJobAssignmentRow[];
  status?: string;
  isMobile?: boolean;
  job_name_to_hide?: string;
  [key: string]: unknown;
}

interface SelectLike {
  value?: string;
  label?: string;
}

export interface CandidateFilterValues {
  search?: string;
  service?: SelectLike[];
  jobs?: SelectLike[];
  job_step?: SelectLike[];
  status?: SelectLike[];
  country?: SelectLike[];
  city?: SelectLike[];
  experience_level?: SelectLike[];
  speciality?: SelectLike[];
  tags?: SelectLike[];
  companies?: SelectLike[];
  sources?: SelectLike[];
  responsible_recruiters?: SelectLike[];
  interacted_by?: SelectLike[];
  team_lead?: SelectLike[];
  added_by?: SelectLike[];
  interviewed_by?: SelectLike[];
  last_interaction?: Array<moment.Moment | null | undefined>;
  interview_date?: Array<moment.Moment | null | undefined>;
  added_during_date?: Array<moment.Moment | null | undefined>;
  [key: string]: unknown;
}

export interface CandidateTableOptions {
  onlyInteracted: boolean;
  sorting: { key?: string; order: string; field: string };
  limit: { value: number; label: number };
  page: number;
  offset: number;
  itemsCount: number;
}

export const candidatesListMapper = (list: CandidateListApiItem[]): MappedCandidateRow[] =>
  list.map(c => ({
    candidate: {
      uuid: c.uuid,
      fullNameEn: c.full_name_latin,
      fullNameRu: c.full_name_local,
    },
    speciality_name: c.speciality_name,
    job_assignments: c.job_assignments ?? [],
    status: c.status,
  }));

export const getCandidateStatusColor = (status?: string): StatusType => {
  switch (status) {
    case STATUSES.ACTIVE:
    case STATUSES.HIRED:
      return STATUS_TYPES.GREEN;
    case STATUSES.REJECTED:
    case STATUSES.FIRED:
      return STATUS_TYPES.RED;
    case STATUSES.BLACKLISTED:
      return STATUS_TYPES.BLACK;
    default:
      return STATUS_TYPES.GRAY;
  }
};

export const getJobStatusColor = (status?: string): StatusType => {
  switch (status) {
    case stepsStatuses.scheduleInterview:
    case stepsStatuses.receiveFeedback:
    case stepsStatuses.sendOffer:
      return STATUS_TYPES.GRAY;
    case stepsStatuses.attendInterview:
    case stepsStatuses.provideFeedback:
      return STATUS_TYPES.YELLOW;
    case stepsStatuses.waitForResolution:
    case stepsStatuses.waitingForFinalDecision:
      return STATUS_TYPES.BLUE;
    case stepsStatuses.rejected:
    case STATUSES.FIRED:
      return STATUS_TYPES.RED;
    case STATUSES.HIRED:
      return STATUS_TYPES.GREEN;
    default:
      return STATUS_TYPES.BLUE;
  }
};

const simpleMapper = (array: SelectLike[]) => array.map(({ value }) => value).join(',');
const dateModifier = (date: moment.Moment | null | undefined) => date && moment(date).format('YYYY-MM-DD');

export const filtersMapper = ({ search, ...values }: CandidateFilterValues) => ({
  search,
  service: simpleMapper(values.service as SelectLike[]),
  jobs: simpleMapper(values.jobs as SelectLike[]),
  job_step: simpleMapper(values.job_step as SelectLike[]),
  status: simpleMapper(values.status as SelectLike[]),
  country: simpleMapper(values.country as SelectLike[]),
  city: values.city && simpleMapper(values.city),
  level: simpleMapper(values.experience_level as SelectLike[]),
  speciality: simpleMapper(values.speciality as SelectLike[]),
  tags: simpleMapper(values.tags as SelectLike[]),
  companies: simpleMapper(values.companies as SelectLike[]),
  sources: simpleMapper(values.sources as SelectLike[]),
  responsible_recruiters: simpleMapper(values.responsible_recruiters as SelectLike[]),
  interacted_by: simpleMapper(values.interacted_by as SelectLike[]),
  team_lead: simpleMapper(values.team_lead as SelectLike[]),
  added_by: simpleMapper(values.added_by as SelectLike[]),
  interviewed_by: simpleMapper(values.interviewed_by as SelectLike[]),
  last_interaction_after: dateModifier(values.last_interaction?.[0]),
  last_interaction_before: dateModifier(values.last_interaction?.[1]),
  interviewed_date_after: dateModifier(values.interview_date?.[0]),
  interviewed_date_before: dateModifier(values.interview_date?.[1]),
  added_during_date_after: dateModifier(values.added_during_date?.[0]),
  added_during_date_before: dateModifier(values.added_during_date?.[1]),
});

export const tableOptionsMapper = (options: CandidateTableOptions) => ({
  limit: options.limit.value,
  offset: options.offset,
  sorting_field: options.sorting.field,
  sorting: options.sorting.order,
  only_interacted: options.onlyInteracted,
});
